import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import ComplianceRing from "@/components/ComplianceRing";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import {
  UtensilsCrossed, CalendarCheck, ChevronRight,
  Check, CheckCircle2, XCircle, Clock, Loader2,
  Sun, Cloud, Moon, Sparkles, Leaf, ArrowUpRight, Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import { scheduleSmartReminders } from "@/utils/notifications";
import { toast } from "sonner";

const SLOT_RE = /^\[(morning|midday|evening)\] /;
const getSlot = (text: string) => {
  if (text.startsWith("[morning]")) return "morning" as const;
  if (text.startsWith("[midday]"))  return "midday"  as const;
  if (text.startsWith("[evening]")) return "evening" as const;
  return "general" as const;
};
const stripSlot = (text: string) => text.replace(SLOT_RE, "");
const cleanText = (text: string) =>
  stripSlot(text).replace(/ — \d+\+\d+\+\d+.*$/, "");
const SLOT_META = {
  morning: { labelEn: "Morning", labelBn: "সকাল",  Icon: Sun,             color: "text-[#2c7772]", bg: "bg-[#d8f2ed]" },
  midday:  { labelEn: "Midday",  labelBn: "দুপুর",  Icon: Cloud,           color: "text-[#3e9f90]", bg: "bg-[#e0f4ef]" },
  evening: { labelEn: "Night",   labelBn: "রাত",    Icon: Moon,            color: "text-[#174e49]", bg: "bg-[#ccefe4]" },
  general: { labelEn: "",        labelBn: "",        Icon: UtensilsCrossed, color: "text-primary",    bg: "bg-primary/10"    },
} as const;

const todayStr = () => new Date().toISOString().split("T")[0];

function processTasks(raw: any[]): any[] {
  const today = todayStr();
  const nowMs = Date.now();
  const ONE_DAY_MS = 1000 * 60 * 60 * 24;

  const processed = raw
    .map((task) => {
      const createdMs = new Date(task.created_at).getTime();
      const daysElapsed = Math.floor((nowMs - createdMs) / ONE_DAY_MS);
      const isExpired =
        task.days_remaining !== null &&
        task.days_remaining > 0 &&
        daysElapsed >= task.days_remaining;
      const remainingDays =
        task.days_remaining !== null && task.days_remaining > 0
          ? Math.max(0, task.days_remaining - daysElapsed)
          : null;
      const updatedDate = task.updated_at
        ? new Date(task.updated_at).toISOString().split("T")[0]
        : null;
      const doneToday = task.done === true && updatedDate === today;
      return { ...task, done: doneToday, isExpired, remainingDays };
    })
    .filter((task) => !task.isExpired);

  // Deduplicate by text — keep the most recent entry per unique text
  const seen = new Map<string, any>();
  for (const task of processed) {
    const existing = seen.get(task.text);
    if (!existing || new Date(task.created_at) > new Date(existing.created_at)) {
      seen.set(task.text, task);
    }
  }
  return Array.from(seen.values());
}

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const PatientDashboard = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [appointment, setAppointment] = useState<any>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const uid = user.id;

    Promise.all([
      supabase.from("profiles").select("*").eq("user_id", uid).single(),
      supabase.from("tasks").select("*").eq("patient_id", uid).order("created_at", { ascending: false }),
      supabase.from("consultation_requests").select("*").eq("patient_id", uid).order("created_at", { ascending: false }).limit(1).maybeSingle(),
    ]).then(async ([profileRes, tasksRes, apptRes]) => {
      setProfile(profileRes.data);
      const processed = processTasks(tasksRes.data ?? []);
      setTasks(processed);
      scheduleSmartReminders(processed);
      if (apptRes.data) {
        const p = apptRes.data as any;
        const { data: practProfile } = await supabase
          .from("profiles")
          .select("full_name, role")
          .eq("user_id", p.practitioner_id)
          .single();
        setAppointment({
          practitionerName: practProfile?.full_name ?? "",
          type: p.practitioner_type,
          status: p.status,
          time: new Date(p.created_at).toLocaleDateString(),
        });
      }
    }).finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const toggleTask = async (taskId: string, currentDone: boolean) => {
    if (togglingId === taskId) return;
    setTogglingId(taskId);
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, done: !currentDone } : t));
    try {
      const { error } = await supabase.from("tasks").update({ done: !currentDone }).eq("id", taskId);
      if (error) {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, done: currentDone } : t));
        toast.error(error.message);
      }
    } finally {
      setTogglingId(null);
    }
  };

  const patientName = profile?.full_name ?? "";
  const completedTasks = tasks.filter(t => t.done).length;
  const totalTasks = tasks.length;
  const complianceScore = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const statusIcon = (status: string) => {
    if (status === "accepted") return <CheckCircle2 className="h-4 w-4 text-success" />;
    if (status === "rejected") return <XCircle className="h-4 w-4 text-destructive" />;
    return <Clock className="h-4 w-4 text-warning" />;
  };

  const statusText = (status: string) => {
    if (status === "accepted") return bn ? "গৃহীত" : "Accepted";
    if (status === "rejected") return bn ? "প্রত্যাখ্যাত" : "Rejected";
    return bn ? "অপেক্ষমান" : "Pending";
  };

  return (
    <div className="min-h-screen bg-[#fbfcf8] pb-28">
      <div className="page-shell px-4 pb-8 pt-5 sm:px-5 sm:pt-7">
        <header className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Sebaco" className="h-5 w-5 object-contain" />
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[#174e49]">
                SEBACO · {bn ? "আপনার স্বাস্থ্যস্থান" : "Your health space"}
              </p>
            </div>
            <LanguageToggle />
          </div>
          <div className="relative mt-5 min-h-[132px] pr-36">
            <h1 className="font-serif text-[28px] font-extrabold leading-[1.12] tracking-tight text-[#102d2a]">
              {t("dashboard.greeting")},<br />
              <span>{patientName || (bn ? "রোগী" : "Patient")}</span>
            </h1>
            <p className="mt-2 max-w-[235px] text-sm leading-5 text-[#5d6f6b]">
              {bn ? "আজ আপনার স্বাস্থ্যের জন্য একটি ছোট পদক্ষেপ নিন।" : "Let’s take a small step towards better health today."}
            </p>
            <div className="absolute right-0 top-3 h-28 w-32 overflow-hidden rounded-t-full rounded-b-none border border-[#e2eee6] bg-[#eff7ed]">
              <img src="/sun-landscape.jpg" alt="" className="h-full w-full scale-[1.1] object-cover object-[35%_25%]" />
            </div>
          </div>
        </header>

        <motion.section
          className="relative mb-4 min-h-[202px] overflow-hidden rounded-[18px] border border-[#dbeae1] bg-[#f0f8ef] px-5 py-5"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="relative z-10 max-w-[57%]">
            <div className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-[#e2f1e5] px-2.5 py-1 text-[10px] font-bold text-[#236b5c]">
              <Leaf className="h-3.5 w-3.5" />
              {bn ? "আজকের যত্নের টিপ" : "Daily tip"}
            </div>
            <h2 className="font-serif text-[19px] font-extrabold leading-6 text-[#174e49]">
              {bn ? "নিজের যত্ন নিন, প্রতিদিন একটি ছোট পদক্ষেপ।" : "Take care of yourself, one small step at a time."}
            </h2>
            <p className="mt-2 text-xs leading-5 text-[#52706f]">
              {bn ? "ছোট অভ্যাস আজকে, আরও ভালো স্বাস্থ্য আগামীকাল।" : "Small steps today, healthier tomorrow."}
            </p>
              <Link to="/patient-tips" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#c9ddd0] bg-[#fbfcf8] px-3.5 py-2 text-[11px] font-bold text-[#174e49]">
              {bn ? "টিপ দেখুন" : "Explore tips"} <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <img src="/daily-tip-girl.png" alt="" className="pointer-events-none absolute -bottom-1 -right-8 h-[184px] w-[250px] object-contain object-right-bottom" />
        </motion.section>

        <motion.section className="mb-4 rounded-[18px] border border-[#e2ebe4] bg-white p-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-3 flex items-start justify-between">
              <div>
                <p className="text-sm font-bold">{bn ? "যত্নের ধারাবাহিকতা" : "Care consistency"}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{bn ? "আজকের অগ্রগতি" : "Today’s progress"}</p>
              </div>
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex items-center gap-4">
              <ComplianceRing score={complianceScore} size={84} strokeWidth={7} />
              <p className="text-xs leading-5 text-muted-foreground">
                {totalTasks > 0
                  ? (bn ? `${totalTasks}টির মধ্যে ${completedTasks}টি সম্পন্ন` : `${completedTasks} of ${totalTasks} planned actions complete`)
                  : (bn ? "আপনার পরিকল্পিত কাজ এখানে দেখা যাবে।" : "Your planned actions will appear here.")}
              </p>
            </div>
        </motion.section>

        <motion.section className="health-tile mint mb-5 flex min-h-[146px] flex-col justify-between rounded-[18px] p-4 shadow-none" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold">{bn ? "পরবর্তী অ্যাপয়েন্টমেন্ট" : "Next appointment"}</p>
                {appointment ? (
                  <>
                    <p className="mt-1.5 font-serif text-xl font-extrabold">{appointment.practitionerName}</p>
                    <p className="mt-0.5 text-xs opacity-80">{appointment.type === "doctor" ? (bn ? "ডাক্তারের পরামর্শ" : "Doctor consultation") : (bn ? "পুষ্টি ফলো-আপ" : "Nutrition follow-up")}</p>
                  </>
                ) : (
                  <p className="mt-2 font-serif text-xl font-extrabold">{bn ? "বিশেষজ্ঞ খুঁজুন" : "Find your care team"}</p>
                )}
              </div>
              <CalendarCheck className="h-5 w-5 opacity-75" />
            </div>
            {appointment ? (
              <div className="flex items-center justify-between gap-2 text-[10px] font-bold">
                <span className="flex items-center gap-1.5 rounded-full bg-white/35 px-2.5 py-1.5">
                  {statusIcon(appointment.status)} {statusText(appointment.status)} · {appointment.time}
                </span>
                <ArrowUpRight className="h-4 w-4" />
              </div>
            ) : (
              <Link to="/find-dietitian" className="flex w-fit items-center gap-1 text-xs font-bold">{bn ? "বিশেষজ্ঞ খুঁজুন" : "Find a practitioner"} <ChevronRight className="h-3 w-3" /></Link>
            )}
        </motion.section>

        <section className="mb-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-serif text-base font-extrabold">{bn ? "আজকের টাস্ক" : "Today’s tasks"}</h2>
            <Link to="/patient-tasks" className="flex items-center gap-1 text-[11px] font-bold text-primary">{t("common.see_all")} <ChevronRight className="h-3 w-3" /></Link>
          </div>
          {tasks.length === 0 ? (
            <div className="rounded-[18px] border border-[#e2ebe4] bg-white p-7 text-center">
              <Activity className="mx-auto mb-2 h-7 w-7 text-primary/60" />
              <p className="text-sm text-muted-foreground">{bn ? "আজকের কোনো টাস্ক নেই" : "No tasks for today"}</p>
            </div>
          ) : (
            <motion.div className="space-y-2 rounded-[18px] border border-[#e2ebe4] bg-white p-3" variants={stagger} initial="hidden" animate="show">
              {tasks.slice(0, 4).map((task) => (
                <motion.button
                  key={task.id}
                  variants={fadeUp}
                  onClick={() => toggleTask(task.id, task.done)}
                  disabled={togglingId === task.id}
                  className={`flex w-full items-center gap-3 rounded-2xl border border-[#edf1ed] bg-[#fbfcf8] px-3 py-2.5 text-left transition-all active:scale-[0.99] ${task.done ? "opacity-60" : "hover:bg-white"}`}
                >
                  {(() => {
                    const slot = getSlot(task.text);
                    const meta = SLOT_META[slot];
                    const SlotIcon = meta.Icon;
                    return <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${meta.bg}`}><SlotIcon className={`h-4 w-4 ${meta.color}`} /></div>;
                  })()}
                  <div className="min-w-0 flex-1">
                    <span className={`block truncate text-xs font-bold ${task.done ? "line-through text-muted-foreground" : "text-foreground"}`}>{cleanText(task.text)}</span>
                    <span className="text-[10px] text-muted-foreground">{getSlot(task.text) === "general" ? (bn ? "আজকের পরিকল্পনা" : "Today’s plan") : (bn ? SLOT_META[getSlot(task.text)].labelBn : SLOT_META[getSlot(task.text)].labelEn)}</span>
                  </div>
                  <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${task.done ? "border-primary bg-primary" : "border-muted-foreground/40"}`}>
                    {task.done && <Check className="h-3 w-3 text-primary-foreground" />}
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </section>

      </div>
    </div>
  );
};

export default PatientDashboard;
